import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Button, Card, Icon, Input, Select, Spinner, StatusPill, Textarea, useToast
} from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { brl, datePt, weekdayLong, todayIso } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface BusinessRow {
  id: string; name: string; description: string | null; category_key: string | null;
  whatsapp_phone: string | null; onboarding_step: number; is_public: number;
}
interface ServiceRow { id: string; name: string; price_cents: number; duration_minutes: number; status: string }
interface Category { id: string; key: string; label: string }
interface HoursRow { weekday: number; opensAt: string; closesAt: string; breakStart: string | null; breakEnd: string | null }
interface ExceptionRow { id: string; type: string; start_date: string; end_date: string; reason: string | null; professionalName: string | null }
interface Fail { status: number; message: string }

function toFail(err: unknown): Fail {
  if (err instanceof ApiError) return { status: err.status, message: err.message };
  return { status: 0, message: err instanceof Error ? err.message : tr('Não foi possível concluir a operação.') };
}

const STEPS = [
  { key: 'info', label: 'Informações', optional: false },
  { key: 'category', label: 'Categoria', optional: false },
  { key: 'services', label: 'Serviços e preços', optional: false },
  { key: 'duration', label: 'Duração', optional: false },
  { key: 'hours', label: 'Dias e horários', optional: false },
  { key: 'days_off', label: 'Folgas e bloqueios', optional: true },
  { key: 'photo', label: 'Foto e descrição', optional: true },
  { key: 'whatsapp', label: 'WhatsApp', optional: true },
  { key: 'review', label: 'Revisão', optional: false },
  { key: 'publish', label: 'Publicação', optional: false }
];

function normalizePhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 11 || digits.length === 10) return `55${digits}`;
  if (digits.length === 12 && digits.startsWith('55')) return digits;
  if (digits.length >= 8) return digits;
  return null;
}

export function PanelOnboardingPage(): JSX.Element {
  usePageMeta(tr('Configuração'), tr('Assistente para configurar seu estabelecimento.'), '/painel/configurar');
  const toast = useToast();
  const { active } = useAuth();
  const bid = active?.businessId ?? '';
  const role = active?.role ?? 'professional';
  const perms = active?.permissions ?? [];
  const canSetup = role === 'owner' || (role === 'manager' && (perms.includes('*') || perms.includes('business_settings')));

  const [cur, setCur] = useState(() => Math.min(Math.max((active?.onboardingStep ?? 1) - 1, 0), STEPS.length - 1));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Fail | null>(null);
  const [error, setError] = useState<Fail | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const [business, setBusiness] = useState<BusinessRow | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryKey, setCategoryKey] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [services, setServices] = useState<ServiceRow[]>([]);

  const [svcName, setSvcName] = useState('');
  const [svcPrice, setSvcPrice] = useState('');
  const [svcDuration, setSvcDuration] = useState('60');
  const [svcCategory, setSvcCategory] = useState('');

  const [durations, setDurations] = useState<Record<string, string>>({});
  const [hours, setHours] = useState<Record<number, { open: boolean; opensAt: string; closesAt: string }>>(
    () => Object.fromEntries([0, 1, 2, 3, 4, 5, 6].map((i) => [i, { open: [1, 2, 3, 4, 5].includes(i), opensAt: '09:00', closesAt: '18:00' }]))
  );
  const [exceptions, setExceptions] = useState<ExceptionRow[]>([]);
  const [excType, setExcType] = useState('day_off');
  const [excFrom, setExcFrom] = useState(todayIso());
  const [excTo, setExcTo] = useState(todayIso());
  const [excReason, setExcReason] = useState('');

  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [photoPath, setPhotoPath] = useState<string | null>(null);
  const [whatsapp, setWhatsatsapp] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!bid) { setLoading(false); return; }
    setLoading(true);
    setLoadError(null);
    try {
      const b = await api.get<{ business: BusinessRow }>(`/api/businesses/${bid}`);
      setBusiness(b.business);
      setName(b.business.name);
      setDescription(b.business.description ?? '');
      setCategoryKey(b.business.category_key ?? '');
      setWhatsatsapp(b.business.whatsapp_phone ?? '');
      const s = await api.get<{ services: ServiceRow[]; categories: Category[] }>(`/api/services/${bid}/services`);
      setServices(s.services);
      setCategories(s.categories);
      setDurations(Object.fromEntries(s.services.map((x) => [x.id, String(x.duration_minutes)])));
      const h = await api.get<{ businessHours: HoursRow[] }>(`/api/schedule/${bid}/hours`);
      if (h.businessHours.length) {
        setHours((prev) => {
          const next = { ...prev };
          for (let i = 0; i < 7; i++) next[i] = { open: false, opensAt: '09:00', closesAt: '18:00' };
          for (const r of h.businessHours) next[r.weekday] = { open: true, opensAt: r.opensAt, closesAt: r.closesAt };
          return next;
        });
      }
      const e = await api.get<{ exceptions: ExceptionRow[] }>(`/api/schedule/${bid}/exceptions?from=${todayIso()}`);
      setExceptions(e.exceptions);
    } catch (err) {
      setLoadError(toFail(err));
    } finally {
      setLoading(false);
    }
  }, [bid]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patchStep(step: number, extra?: Record<string, unknown>): Promise<void> {
    await api.patch(`/api/businesses/${bid}`, { onboardingStep: step, ...(extra ?? {}) });
    setBusiness((prev) => (prev ? { ...prev, onboarding_step: step } : prev));
  }

  const goNext = (after?: () => Promise<void>): void => {
    setSaving(true);
    setError(null);
    void (async () => {
      try {
        if (after) await after();
        await patchStep(cur + 1 <= 10 ? cur + 1 : 10);
        setCur((c) => Math.min(c + 1, STEPS.length - 1));
        setDone(false);
      } catch (err) {
        setError(toFail(err));
      } finally {
        setSaving(false);
      }
    })();
  };

  const skip = (): void => {
    setCur((c) => Math.min(c + 1, STEPS.length - 1));
    void patchStep(cur + 2 <= 10 ? cur + 2 : 10).catch((err) => setError(toFail(err)));
  };

  async function uploadPhoto(file: File): Promise<void> {
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setError({ status: 0, message: 'Use JPG, PNG, WEBP ou GIF.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError({ status: 0, message: tr('Imagem acima de 5 MB.') });
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(new Error(tr('Falha ao ler arquivo.')));
      r.readAsDataURL(file);
    });
    const res = await api.post<{ path: string }>(`/api/businesses/${bid}/upload`, { filename: file.name, data: dataUrl });
    setPhotoPath(res.path);
  }

  async function saveBusinessInfo(): Promise<void> {
    await api.patch(`/api/businesses/${bid}`, { name: name.trim(), description: description.trim() });
  }

  async function saveCategory(): Promise<void> {
    await api.patch(`/api/businesses/${bid}`, { categoryKey });
  }

  async function addService(): Promise<void> {
    const cents = Math.round(Number(svcPrice.replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents < 0) throw new Error(tr('Preço inválido.'));
    const dur = Number(svcDuration);
    await api.post(`/api/services/${bid}/services`, {
      name: svcName.trim(),
      priceCents: cents,
      durationMinutes: dur,
      categoryKey: svcCategory || undefined,
      onlineBooking: true
    });
    const s = await api.get<{ services: ServiceRow[]; categories: Category[] }>(`/api/services/${bid}/services`);
    setServices(s.services);
    setDurations(Object.fromEntries(s.services.map((x) => [x.id, String(x.duration_minutes)])));
    setSvcName(''); setSvcPrice(''); setSvcDuration('60'); setSvcCategory('');
  }

  async function saveDurations(): Promise<void> {
    for (const svc of services) {
      const val = Number(durations[svc.id] ?? svc.duration_minutes);
      if (val !== svc.duration_minutes && val >= 5) {
        await api.patch(`/api/services/${bid}/services/${svc.id}`, { durationMinutes: val });
      }
    }
  }

  async function saveHours(): Promise<void> {
    const entries = [0, 1, 2, 3, 4, 5, 6].map((i) => {
      const h = hours[i];
      return h.open ? { weekday: i, opensAt: h.opensAt, closesAt: h.closesAt, breakStart: null, breakEnd: null } : null;
    }).filter((x): x is NonNullable<typeof x> => x !== null);
    await api.put(`/api/schedule/${bid}/hours`, { entries });
  }

  async function addException(): Promise<void> {
    await api.post(`/api/schedule/${bid}/exceptions`, {
      type: excType,
      startDate: excFrom,
      endDate: excTo,
      reason: excReason.trim() || undefined
    });
    const e = await api.get<{ exceptions: ExceptionRow[] }>(`/api/schedule/${bid}/exceptions?from=${todayIso()}`);
    setExceptions(e.exceptions);
    setExcReason('');
  }

  async function saveProfileStep(extra: Record<string, unknown>): Promise<void> {
    if (!active?.professionalId) throw new Error(tr('Nenhum perfil profissional vinculado. Conclua a publicação.'));
    await api.patch(`/api/businesses/${bid}/professional-me`, extra);
  }

  async function publish(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await api.post<{ publicUrl: string }>(`/api/businesses/${bid}/publish`);
      setBusiness((prev) => (prev ? { ...prev, is_public: 1, onboarding_step: 10 } : prev));
      setDone(true);
      toast('Perfil publicado!', 'success');
    } catch (err) {
      setError(toFail(err));
    } finally {
      setSaving(false);
    }
  }

  const step = STEPS[cur];
  const waPreview = normalizePhone(whatsapp);
  const openDays = [0, 1, 2, 3, 4, 5, 6].filter((i) => hours[i]?.open);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Configuração inicial")}</h1>
          <p className="text-sm text-muted">{tr("Passo")} {cur + 1} {tr("de")} {STEPS.length}: {tr(step.label)}</p>
        </div>
        <StatusPill tone={business?.is_public ? 'positive' : 'neutral'}>{business?.is_public ? tr('Publicado') : tr('Em configuração')}</StatusPill>
      </div>

      <Card as="section">
        <ol className="flex flex-wrap gap-1.5 text-xs">
          {STEPS.map((s, i) => (
            <li key={s.key}>
              <button
                type="button"
                onClick={() => i <= cur && setCur(i)}
                disabled={i > cur}
                className={`rounded-full px-3 py-1 font-semibold ${i === cur ? 'bg-accent text-on-accent' : i < cur ? 'bg-surface text-graphite' : 'bg-surface text-muted'}`}
              >
                {i + 1}. {tr(s.label)}{s.optional ? tr(' (opcional)') : ''}
              </button>
            </li>
          ))}
        </ol>
      </Card>

      {loadError ? <ErrorAlert error={loadError} /> : null}
      {error ? <ErrorAlert error={error} /> : null}
      {!canSetup ? <Alert tone="warning" title={tr("Permissão insuficiente")}>{tr("Seu papel não permite concluir a configuração do estabelecimento. O proprietário ou um gerente com permissão precisa executar estas etapas.")}</Alert> : null}

      {loading ? (
        <Card><Spinner label={tr("Carregando…")} /></Card>
      ) : (
        <Card as="section">
          <div className="space-y-4">
            {step.key === 'info' && (
              <>
                <label className="block">
                  <span className="nl-label">{tr("Nome do estabelecimento")}</span>
                  <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canSetup} maxLength={120} />
                </label>
                <label className="block">
                  <span className="nl-label">{tr("Descrição")}</span>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} disabled={!canSetup} maxLength={2000} />
                </label>
              </>
            )}

            {step.key === 'category' && (
              <label className="block">
                <span className="nl-label">{tr("Categoria principal")}</span>
                <Select value={categoryKey} onChange={(e) => setCategoryKey(e.target.value)} disabled={!canSetup}>
                  <option value="">{tr("— Selecione —")}</option>
                  {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </Select>
                <span className="nl-hint mt-1 block">{tr("Categorias definidas pela administração da plataforma.")}</span>
              </label>
            )}

            {step.key === 'services' && (
              <>
                <p className="text-sm text-muted">{tr("Cadastre ao menos um serviço ativo — é obrigatório para publicar.")}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input placeholder={tr("Nome do serviço")} value={svcName} onChange={(e) => setSvcName(e.target.value)} disabled={!canSetup} />
                  <Input placeholder={tr("Preço (R$)")} inputMode="decimal" value={svcPrice} onChange={(e) => setSvcPrice(e.target.value)} disabled={!canSetup} />
                  <label className="flex items-center gap-2 text-sm">
                    <span className="text-muted">{tr("Duração (min)")}</span>
                    <Input type="number" min={5} value={svcDuration} onChange={(e) => setSvcDuration(e.target.value)} disabled={!canSetup} />
                  </label>
                  <Select value={svcCategory} onChange={(e) => setSvcCategory(e.target.value)} disabled={!canSetup}>
                    <option value="">{tr("Sem categoria")}</option>
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </Select>
                </div>
                <Button variant="secondary" disabled={!canSetup || !svcName.trim() || !svcPrice.trim()} onClick={() => {
                  setError(null);
                  void addService().catch((err) => setError(toFail(err)));
                }}>{tr("Adicionar serviço")}</Button>
                {services.length === 0 ? <p className="text-sm text-muted">{tr("Nenhum serviço ainda.")}</p> : (
                  <ul className="space-y-1 text-sm">
                    {services.map((s) => <li key={s.id} className="flex justify-between gap-2"><span>{s.name}</span><span className="text-muted">{brl(s.price_cents)} · {s.duration_minutes}{tr("min")}</span></li>)}
                  </ul>
                )}
              </>
            )}

            {step.key === 'duration' && (
              services.length === 0 ? <p className="text-sm text-muted">{tr("Cadastre serviços na etapa anterior primeiro.")}</p> : (
                <div className="space-y-2">
                  {services.map((s) => (
                    <label key={s.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate">{s.name}</span>
                      <span className="flex shrink-0 items-center gap-2">
                        <Input type="number" min={5} value={durations[s.id] ?? String(s.duration_minutes)} disabled={!canSetup}
                          onChange={(e) => setDurations((prev) => ({ ...prev, [s.id]: e.target.value }))} className="w-24" />
                        <span className="text-muted">{tr("min")}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )
            )}

            {step.key === 'hours' && (
              <div className="space-y-2">
                <p className="text-sm text-muted">{tr("Defina o expediente. É obrigatório para publicar.")}</p>
                {[0, 1, 2, 3, 4, 5, 6].map((i) => {
                  const h = hours[i];
                  return (
                    <div key={i} className="flex flex-wrap items-center gap-3 text-sm">
                      <label className="flex w-28 items-center gap-2">
                        <input type="checkbox" checked={h.open} disabled={!canSetup}
                          onChange={(e) => setHours((prev) => ({ ...prev, [i]: { ...prev[i], open: e.target.checked } }))} />
                        {weekdayLong(i)}
                      </label>
                      <Input type="time" value={h.opensAt} disabled={!canSetup || !h.open}
                        onChange={(e) => setHours((prev) => ({ ...prev, [i]: { ...prev[i], opensAt: e.target.value } }))} className="w-32" />
                      <span className="text-muted">{tr("às")}</span>
                      <Input type="time" value={h.closesAt} disabled={!canSetup || !h.open}
                        onChange={(e) => setHours((prev) => ({ ...prev, [i]: { ...prev[i], closesAt: e.target.value } }))} className="w-32" />
                    </div>
                  );
                })}
              </div>
            )}

            {step.key === 'days_off' && (
              <div className="space-y-3">
                <p className="text-sm text-muted">{tr("Opcional. Registre folgas, feriados ou férias (dia inteiro).")}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Select value={excType} onChange={(e) => setExcType(e.target.value)} disabled={!canSetup}>
                    <option value="day_off">{tr("Folga")}</option>
                    <option value="holiday">{tr("Feriado")}</option>
                    <option value="vacation">{tr("Férias")}</option>
                  </Select>
                  <Input type="text" placeholder={tr("Motivo (opcional)")} value={excReason} onChange={(e) => setExcReason(e.target.value)} disabled={!canSetup} />
                  <label className="text-sm"><span className="nl-label">{tr("De")}</span><Input type="date" value={excFrom} min={todayIso()} onChange={(e) => setExcFrom(e.target.value)} disabled={!canSetup} /></label>
                  <label className="text-sm"><span className="nl-label">{tr("Até")}</span><Input type="date" value={excTo} min={excFrom} onChange={(e) => setExcTo(e.target.value)} disabled={!canSetup} /></label>
                </div>
                <Button variant="secondary" disabled={!canSetup} onClick={() => { setError(null); void addException().catch((err) => setError(toFail(err))); }}>{tr("Adicionar")}</Button>
                {exceptions.length === 0 ? <p className="text-sm text-muted">{tr("Nenhuma exceção.")}</p> : (
                  <ul className="space-y-1 text-sm">
                    {exceptions.map((x) => <li key={x.id} className="flex justify-between gap-2"><span>{x.type}</span><span className="text-muted">{datePt(x.start_date)} – {datePt(x.end_date)}</span></li>)}
                  </ul>
                )}
              </div>
            )}

            {step.key === 'photo' && (
              !active?.professionalId ? <p className="text-sm text-muted">{tr("Nenhum perfil profissional vinculado a esta conta.")}</p> : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-16 w-16 overflow-hidden rounded-xl border border-line bg-surface">
                      {photoPath ? <img src={photoPath} alt={tr("Foto")} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted"><Icon name="user" /></div>}
                    </div>
                    <Button variant="secondary" disabled={!canSetup} onClick={() => fileRef.current?.click()}>{tr("Enviar foto")}</Button>
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => {
                      const f = e.target.files?.[0]; e.target.value = '';
                      if (f) void uploadPhoto(f).catch((err) => setError(toFail(err)));
                    }} />
                  </div>
                  <label className="block"><span className="nl-label">{tr("Título")}</span><Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canSetup} maxLength={120} /></label>
                  <label className="block"><span className="nl-label">{tr("Descrição")}</span><Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} disabled={!canSetup} maxLength={2000} /></label>
                </div>
              )
            )}

            {step.key === 'whatsapp' && (
              <div className="space-y-2">
                <label className="block">
                  <span className="nl-label">{tr("WhatsApp (internacional)")}</span>
                  <Input value={whatsapp} onChange={(e) => setWhatsatsapp(e.target.value)} disabled={!canSetup} invalid={whatsapp.trim().length > 0 && (waPreview === null || waPreview.length < 12)} placeholder="5511999998888" />
                  <span className="nl-hint mt-1 block">{waPreview ? tr('Será salvo como: {value}', { value: waPreview }) : tr('Formato: 55 + DDD + número. Usado no botão de contato do perfil.')}</span>
                </label>
              </div>
            )}

            {step.key === 'review' && (
              <dl className="space-y-1.5 text-sm">
                <ReviewRow k={tr('Nome')} v={business?.name ?? '—'} />
                <ReviewRow k={tr('Categoria')} v={categories.find((c) => c.key === categoryKey)?.label ?? tr('Não definida')} />
                <ReviewRow k={tr('Serviços')} v={tr('{count} cadastrado(s)', { count: services.length })} />
                <ReviewRow k={tr('Expediente')} v={openDays.length ? tr('{count} dia(s) abertos', { count: openDays.length }) : tr('Nenhum dia configurado')} />
                <ReviewRow k={tr('Folgas')} v={tr('{count} exceção(ões)', { count: exceptions.length })} />
                <ReviewRow k={tr('Foto do perfil')} v={photoPath ? tr('Enviada') : tr('Sem foto')} />
                <ReviewRow k={tr('WhatsApp')} v={waPreview ?? tr('Não informado')} />
              </dl>
            )}

            {step.key === 'publish' && (
              done ? (
                <Alert tone="success" title={tr("Tudo pronto!")}>{tr("Seu estabelecimento está publicado.")} <Link className="font-semibold underline" to="/painel">{tr("Voltar ao painel")}</Link>.</Alert>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted">{tr("Ao publicar, o servidor valida que você tem ao menos um serviço ativo e o expediente configurado. Se faltar algo, ele informa o que falta.")}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-muted">
                    <span className="rounded bg-surface px-2 py-1">{services.length} {tr("serviço(s)")}</span>
                    <span className="rounded bg-surface px-2 py-1">{openDays.length} {tr("dia(s) de expediente")}</span>
                  </div>
                  <Button disabled={!canSetup} loading={saving} onClick={() => void publish()}>{tr("Publicar estabelecimento")}</Button>
                </div>
              )
            )}
          </div>

          {step.key !== 'publish' && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-4">
              <Button variant="ghost" disabled={cur === 0} onClick={() => setCur((c) => Math.max(0, c - 1))}><Icon name="back" size={16} /> {tr("Voltar")}</Button>
              <div className="flex gap-2">
                {step.optional ? <Button variant="secondary" onClick={skip}>{tr("Pular etapa")}</Button> : null}
                <Button
                  loading={saving}
                  disabled={!canSetup}
                  onClick={() => {
                    if (step.key === 'info') return goNext(() => saveBusinessInfo());
                    if (step.key === 'category') return goNext(() => saveCategory());
                    if (step.key === 'duration') return goNext(() => saveDurations());
                    if (step.key === 'hours') return goNext(() => saveHours());
                    if (step.key === 'photo') return goNext(() => { if (active?.professionalId) return saveProfileStep({ photoPath: photoPath ?? '', title: title.trim(), bio: bio.trim() }); return Promise.resolve(); });
                    if (step.key === 'whatsapp') return goNext(() => { if (active?.professionalId) return saveProfileStep({ whatsappPhone: whatsapp.trim() }); return Promise.resolve(); });
                    return goNext();
                  }}
                >
                  {tr("Salvar e continuar")} <Icon name="chevron" size={16} />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function ReviewRow({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-1">
      <dt className="text-muted">{k}</dt><dd className="text-right">{v}</dd>
    </div>
  );
}

function ErrorAlert({ error }: { error: Fail }): JSX.Element {
  const tone: 'warning' | 'danger' = error.status === 402 || error.status === 403 ? 'warning' : 'danger';
  const title = error.status === 402 ? tr('Assinatura necessária') : error.status === 403 ? tr('Permissão insuficiente') : tr('Erro');
  return (
    <Alert tone={tone} title={title}>
      <p>{error.message}</p>
      {error.status === 402 ? <p className="mt-1">{tr("Ative um plano em")} <Link className="font-semibold underline" to="/painel/plano">{tr("Meu plano")}</Link> {tr("para usar o configurador.")}</p> : null}
    </Alert>
  );
}
